import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import { IAppState } from '../../../common/state';
import { AB_COWORKING_POPUP_EXPERIMENT } from '../../../constants/coworkingsExperiment';
import { isAbUseExperimentGroup } from '../../../utils/experiments/isAbUseExperimentGroup';
import { isOfficeRentSearch } from '../../../utils/isOfficeRentSearch';

const LOCAL_STORAGE_COWORKING_HELPER_ITEM = 'isCoworkingHelperClosed';

// ACTION TYPES
export const COWORKING_HELPER_OPENED = 'modals/coworking_helper/MODAL_OPENED';
export const COWORKING_HELPER_CLOSED = 'modals/coworking_helper/MODAL_CLOSED';
export const FILTERS_SCROLLED_DOWN = 'filtersScrolledDown';
export const FILTERS_SCROLLED_UP = 'filtersScrolledUp';

// ACTIONS
export interface ICoworkingHelperOpened {
  type: typeof COWORKING_HELPER_OPENED;
}
export interface ICoworkingHelperClosed {
  type: typeof COWORKING_HELPER_CLOSED;
}

export interface IFiltersScrolledDown {
  type: typeof FILTERS_SCROLLED_DOWN;
}
export interface IFiltersScrolledUp {
  type: typeof FILTERS_SCROLLED_UP;
}

export type TCoworkingHelperActions = ICoworkingHelperOpened | ICoworkingHelperClosed;

export const openCoworkingHelper = (): ICoworkingHelperOpened => ({ type: COWORKING_HELPER_OPENED });
export const closedCoworkingHelper = (): ICoworkingHelperClosed => ({
  type: COWORKING_HELPER_CLOSED,
});
export const filtersScrolledDown = (): IFiltersScrolledDown => ({
  type: FILTERS_SCROLLED_DOWN,
});
export const filtersScrolledUp = (): IFiltersScrolledUp => ({
  type: FILTERS_SCROLLED_UP,
});

// SAGAS
export function* handleCoworkingHelperOpened() {
  const { logger } = (yield select()) as IAppState;

  try {
    sessionStorage.setItem(LOCAL_STORAGE_COWORKING_HELPER_ITEM, 'true');
  } catch (error) {
    logger.warning(error, {
      description: 'Попытка обращения к sessionStorage',
      domain: 'handleCoworkingHelperOpened',
      message: 'Ошибка при записи данных в sessionStorage',
    });
  }
}

export function* handleOfferTypeChanged() {
  const {
    logger,
    filters: { jsonQuery },
    coworkingExperiments: { coworkingHelper },
  } = (yield select()) as IAppState;

  if (!isOfficeRentSearch(jsonQuery) && coworkingHelper.isOpen) {
    yield put(closedCoworkingHelper());

    return;
  }

  try {
    const isCoworkingHelperVisible = sessionStorage.getItem(LOCAL_STORAGE_COWORKING_HELPER_ITEM);

    if (isOfficeRentSearch(jsonQuery) && isCoworkingHelperVisible !== 'true') {
      yield put(openCoworkingHelper());
    }
  } catch (error) {
    logger.warning(error, {
      description: 'Попытка обращения к sessionStorage',
      domain: 'handleCoworkingHelperOpened',
      message: 'Ошибка при записи или чтение данных в sessionStorage',
    });
  }
}

export function* handleFiltersScrolledDown() {
  const {
    coworkingExperiments: { coworkingHelper },
  } = (yield select()) as IAppState;

  if (coworkingHelper.isOpen) {
    yield put(closedCoworkingHelper());
  }
}

export function* handleFiltersScrolledUp() {
  const {
    logger,
    filters: { jsonQuery },
  } = (yield select()) as IAppState;

  try {
    const isCoworkingHelperVisible = sessionStorage.getItem(LOCAL_STORAGE_COWORKING_HELPER_ITEM);

    if (isOfficeRentSearch(jsonQuery) && isCoworkingHelperVisible !== 'true') {
      yield put(openCoworkingHelper());
    }
  } catch (error) {
    logger.warning(error, {
      description: 'Попытка обращения к sessionStorage',
      domain: 'handleFiltersScrolledUp',
      message: 'Ошибка при чтение данных из sessionStorage',
    });
  }
}

export function* coworkingHelperSagas() {
  const {
    abUseExperiments,
    isCoworkingExperimentsEnabled,
    logger,
    filters: { jsonQuery },
  } = (yield select()) as IAppState;

  const isExperimentEnabled: boolean = yield call(
    isAbUseExperimentGroup,
    abUseExperiments,
    AB_COWORKING_POPUP_EXPERIMENT,
    'E',
  );

  if (!isCoworkingExperimentsEnabled || !isExperimentEnabled) {
    return;
  }

  try {
    const isCoworkingHelperVisible = sessionStorage.getItem(LOCAL_STORAGE_COWORKING_HELPER_ITEM);

    if (isOfficeRentSearch(jsonQuery) && isCoworkingHelperVisible !== 'true') {
      yield put(openCoworkingHelper());
      sessionStorage.setItem(LOCAL_STORAGE_COWORKING_HELPER_ITEM, 'true');
    }
  } catch (error) {
    logger.warning(error, {
      description: 'Попытка обращения к sessionStorage',
      domain: 'coworkingHelperSagas',
      message: 'Ошибка при чтение данных из sessionStorage',
    });
  }

  yield all([
    takeLatest(COWORKING_HELPER_OPENED, handleCoworkingHelperOpened),
    takeLatest('filters/offer_type/OFFER_TYPE_SELECTED', handleOfferTypeChanged),
    takeLatest(FILTERS_SCROLLED_DOWN, handleFiltersScrolledDown),
    takeLatest(FILTERS_SCROLLED_UP, handleFiltersScrolledUp),
  ]);
}

// REDUCER
export interface ICoworkingHelper {
  isOpen: boolean;
}

export const initialState = {
  isOpen: false,
};

export const coworkingHelperReducer = (
  state: ICoworkingHelper = initialState,
  action: TCoworkingHelperActions,
): ICoworkingHelper => {
  switch (action.type) {
    case COWORKING_HELPER_OPENED:
      return {
        isOpen: true,
      };

    case COWORKING_HELPER_CLOSED:
      return {
        isOpen: false,
      };

    default:
      return state;
  }
};
